import { Injectable } from '@angular/core';
import { catchError, switchMap, map, tap, withLatestFrom, concatMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, Action } from '@ngrx/store';

import { ProjectDataActions } from './project-data.actions';
import { ProjectEntityService } from 'src/app/core/services/project.service';
import { FileTransferService } from 'src/app/core/services/file-transfer.service';
import { selectProjectEntityTree, selectProjectId } from './project-data.selectors';
import { ProductTreeActionsService } from 'src/app/core/services/productTreeActions.service';
import { ProductTreeService } from 'src/app/core/services/productTree.service';
import { SnackbarActions } from 'src/app/core/store/snackbar/snackbar.actions';
import type { SnackbarData } from 'src/app/core/models/snackbar.model';
import { LoadingActions } from 'src/app/core/store/loading/loading.actions';
import { EntityService } from '../../../core/services/entity.service';
import { ProductViewActions } from '../product-view/product-view.actions';
import { selectMultiEditSelectedEntityIds } from '../product-view/product-view.selectors';

import { selectProductContext } from '../product-context/product-context.selector';
import { Router } from '@angular/router';

@Injectable()
export class ProjectEntityEffects {
  constructor(
    private actions$: Actions,
    private projectEntityService: ProjectEntityService,
    private store: Store,
    private fileTransferService: FileTransferService,
    private productTreeActionsService: ProductTreeActionsService,
    private productTreeService: ProductTreeService,
    private entityService: EntityService,
    private router: Router
  ) {}

  createProjectStart$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ProjectDataActions.create_project_start),
        switchMap(() =>
          this.productTreeService.createEntity(null, 'Project', null, []).pipe(
            map(createdId => ProjectDataActions.create_project_success({ createdId })),
            catchError(error => of(ProjectDataActions.create_project_fail({ error: error })))
          )
        )
      ),
    { dispatch: true }
  );

  updateProjectStart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectDataActions.update_project_start),
      withLatestFrom(this.store.select(selectProjectId)),
      concatMap(([update, projectId]) => {
        return this.entityService
          .updateEntity(projectId, update.entityId, update.propertyKey, update.newValue, update.measurementUnit)
          .pipe(
            switchMap(updatedEntities => {
              const actions: Action<any>[] = [ProjectDataActions.update_project_success({ updatedEntities })];

              if (update.updateFullProject) {
                actions.push(ProjectDataActions.refresh_project());
              }

              if (update.propertyKey === 'GenerateModules') {
                actions.push(ProjectDataActions.redirect_to_product({ projectId: projectId, entityId: update.entityId }));
              }

              return actions;
            })
          );
      })
    )
  );

  updateProjectEntitiesStart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectDataActions.update_project_entities_start),
      withLatestFrom(this.store.select(selectProjectId)),
      concatMap(([update, projectId]) => {
        return this.entityService
          .multiProductUpdateEntity(projectId, update.entityIds, {
            propertyKey: update.propertyKey,
            value: update.newValue,
            measurementUnit: update.measurementUnit
          })
          .pipe(
            switchMap(updatedEntities => {
              const actions: Action<any>[] = [ProjectDataActions.update_project_success({ updatedEntities })];

              return actions;
            })
          );
      })
    )
  );

  updateHoodLinePositionsStart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectDataActions.update_hood_line_positions),
      withLatestFrom(this.store.select(selectProjectId)),
      concatMap(([update, projectId]) => {
        const properties = [
          {
            propertyKey: 'PositionX',
            value: update.position.x
          },
          {
            propertyKey: 'PositionY',
            value: update.position.y
          }
        ];

        return this.entityService.updateEntityProperties(projectId, update.entityId, properties).pipe(
          switchMap(updatedEntities => {
            return [ProjectDataActions.update_project_success({ updatedEntities })];
          })
        );
      })
    )
  );

  redirectToProduct$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ProjectDataActions.redirect_to_product),
        tap(({ projectId, entityId }) =>
          this.router.navigate([`/projects/${projectId}/products`], { queryParams: { selectedId: entityId } })
        )
      ),
    { dispatch: false }
  );

  multiProductUpdate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductViewActions.multi_edit_entity_property_changed),
      withLatestFrom(this.store.select(selectMultiEditSelectedEntityIds)),
      concatMap(([action, multiEditIds]) => {
        return this.entityService
          .multiProductUpdateEntity(action.projectId, multiEditIds, action.propertyChange)
          .pipe(map(updatedEntities => ProjectDataActions.update_project_success({ updatedEntities })));
      })
    )
  );

  getProjectStart$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ProjectDataActions.get_project_start),
        switchMap(data => this.projectEntityService.getProjectEntity(data.id)),
        map(project => {
          return ProjectDataActions.get_project_success({
            entities: project
          });
        }),
        catchError(error => of(ProjectDataActions.get_project_fail({ error: error })))
      ),
    { dispatch: true }
  );

  refreshProjectStart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectDataActions.refresh_project),
      withLatestFrom(this.store.select(selectProjectId)),
      switchMap(([_, projectId]) => {
        if (projectId) {
          return [ProjectDataActions.get_project_start({ id: projectId })];
        }
        return null;
      })
    )
  );

  projectDataUpdated$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectDataActions.update_project_success, ProjectDataActions.get_project_success),
      withLatestFrom(this.store.select(selectProjectId), this.store.select(selectProductContext)),
      switchMap(([_, projectId, context]) => {
        return this.productTreeActionsService.queryProductTreeActions(projectId, context).pipe(
          switchMap(projectActions => {
            return [ProjectDataActions.update_project_actions({ projectActions }), LoadingActions.loading_end()];
          })
        );
      })
    )
  );

  exportXmlStart$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ProjectDataActions.export_xml_start),
        switchMap(() => {
          return [LoadingActions.loading_start(), ProjectDataActions.export_xml_data_request()];
        })
      ),
    { dispatch: true }
  );

  exportXmlDataRequest$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ProjectDataActions.export_xml_data_request),
        withLatestFrom(this.store.select(selectProjectEntityTree)),
        switchMap(([_, magicoreEntity]) => {
          const id = magicoreEntity.Id;
          return this.fileTransferService.exportToXml(id).pipe(
            map(() => ProjectDataActions.export_xml_success()),
            catchError(error => of(ProjectDataActions.export_xml_fail({ error: error })))
          );
        })
      ),
    { dispatch: true }
  );

  exportXmlSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ProjectDataActions.export_xml_success),
        switchMap(() => {
          const data: SnackbarData = {
            type: 'info',
            commonMessage: 'UI.Project.Message.LoadedSuccessfully'
          };
          return [LoadingActions.loading_end(), SnackbarActions.snackbar_open({ snackbarData: data })];
        })
      ),
    { dispatch: true }
  );

  exportMagiCADStart$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ProjectDataActions.export_magicad_start),
        switchMap(() => {
          return [LoadingActions.loading_start(), ProjectDataActions.export_magicad_data_request()];
        })
      ),
    { dispatch: true }
  );

  exportMagiCADDataRequest$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ProjectDataActions.export_magicad_data_request),
        withLatestFrom(this.store.select(selectProjectEntityTree)),
        switchMap(([_, magicoreEntity]) => {
          const id = magicoreEntity.Id;
          return this.fileTransferService.exportToMagiCAD(id).pipe(
            map(() => ProjectDataActions.export_magicad_success()),
            catchError(error => of(ProjectDataActions.export_magicad_fail({ error: error })))
          );
        })
      ),
    { dispatch: true }
  );

  exportMagiCADSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ProjectDataActions.export_magicad_success),
        switchMap(() => {
          const data: SnackbarData = {
            type: 'info',
            commonMessage: 'UI.Project.Message.LoadedSuccessfully'
          };
          return [LoadingActions.loading_end(), SnackbarActions.snackbar_open({ snackbarData: data })];
        })
      ),
    { dispatch: true }
  );

  getReportStart$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ProjectDataActions.get_report_start),
        switchMap(data => {
          return [
            LoadingActions.loading_start(),
            ProjectDataActions.get_report_data_request({ reportType: data.reportType, extension: data.extension })
          ];
        })
      ),
    { dispatch: true }
  );

  getReportDataRequest$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ProjectDataActions.get_report_data_request),
        withLatestFrom(this.store.select(selectProjectEntityTree)),
        switchMap(data => {
          const [action, entity] = data;
          const id = entity.Id;
          const reportLanguagePropertyValue = entity.Properties['ReportLanguage'].Value as string;
          return this.fileTransferService.getReport(action.reportType, id, reportLanguagePropertyValue, action.extension).pipe(
            map(() => ProjectDataActions.get_report_success()),
            catchError(error => of(ProjectDataActions.get_report_fail({ error: error })))
          );
        })
      ),
    { dispatch: true }
  );

  getReportSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ProjectDataActions.get_report_success),
        switchMap(() => {
          const data: SnackbarData = {
            type: 'info',
            commonMessage: 'UI.Project.Message.LoadedSuccessfully'
          };
          return [LoadingActions.loading_end(), SnackbarActions.snackbar_open({ snackbarData: data })];
        })
      ),
    { dispatch: true }
  );

  exportAllDocumentsStart$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ProjectDataActions.export_alldocuments_start),
        switchMap(() => {
          return [LoadingActions.loading_start(), ProjectDataActions.export_alldocuments_data_request()];
        })
      ),
    { dispatch: true }
  );

  exportAllDocumentsDataRequest$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ProjectDataActions.export_alldocuments_data_request),
        withLatestFrom(this.store.select(selectProjectEntityTree)),
        switchMap(([_, magicoreEntity]) => {
          const id = magicoreEntity.Id;
          const reportLanguagePropertyValue = magicoreEntity.Properties['ReportLanguage'].Value as string;
          return this.fileTransferService.exportAllDocuments(id, reportLanguagePropertyValue).pipe(
            map(() => ProjectDataActions.export_alldocuments_success()),
            catchError(error => of(ProjectDataActions.export_alldocuments_fail({ error: error })))
          );
        })
      ),
    { dispatch: true }
  );

  exportAllDocumentsSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ProjectDataActions.export_alldocuments_success),
        switchMap(() => {
          const data: SnackbarData = {
            type: 'info',
            commonMessage: 'UI.Project.Message.LoadedSuccessfully'
          };
          return [LoadingActions.loading_end(), SnackbarActions.snackbar_open({ snackbarData: data })];
        })
      ),
    { dispatch: true }
  );

  createProductEntityStart$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ProjectDataActions.create_entity_start),
        switchMap(data => {
          return this.productTreeService.createEntity(data.entityId, data.productTypeId, data.productName, data.defaultValues).pipe(
            switchMap(createdNodeId => {
              return [
                LoadingActions.loading_end(),
                ProjectDataActions.create_entity_success({ projectId: data.projectId, createdId: createdNodeId })
              ];
            }),
            catchError(error => of(ProjectDataActions.create_entity_fail({ error: error })))
          );
        })
      ),
    { dispatch: true }
  );

  copyProductEntityStart$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ProjectDataActions.copy_entity_start),
        switchMap(data => {
          return this.productTreeService.copy(data.entityId, data.parentId).pipe(
            switchMap(copiedId => {
              return [LoadingActions.loading_end(), ProjectDataActions.copy_entity_success({ copiedId, parentId: data.parentId })];
            }),
            catchError(error => of(ProjectDataActions.copy_entity_fail({ error: error })))
          );
        })
      ),
    { dispatch: true }
  );

  deleteProductEntityStart$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ProjectDataActions.delete_entity_start),
        switchMap(data => {
          return this.productTreeService.delete(data.entityIdList).pipe(
            switchMap(() => {
              return [
                ProjectDataActions.delete_entity_success({
                  deletedIdList: data.entityIdList
                }),
                LoadingActions.loading_end()
              ];
            }),
            catchError(error => of(ProjectDataActions.delete_entity_fail({ error: error })))
          );
        })
      ),
    { dispatch: true }
  );

  exportProjectToPyramidStart$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ProjectDataActions.export_project_to_pyramid_start),
        switchMap(() => {
          return [LoadingActions.loading_start(), ProjectDataActions.export_project_to_pyramid_request()];
        })
      ),
    { dispatch: true }
  );

  exportProjectToPyramidRequest$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ProjectDataActions.export_project_to_pyramid_request),
        withLatestFrom(this.store.select(selectProjectEntityTree)),
        switchMap(([_, magicoreEntity]) => {
          const id = magicoreEntity.Id;
          return this.projectEntityService.exportToPyramid(id).pipe(
            map(() => ProjectDataActions.export_project_to_pyramid_success()),
            catchError(error => of(ProjectDataActions.export_project_to_pyramid_fail({ error: error })))
          );
        })
      ),
    { dispatch: true }
  );

  exportProjectToPyramidSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ProjectDataActions.export_project_to_pyramid_success),
        switchMap(() => {
          const data: SnackbarData = {
            type: 'info',
            commonMessage: 'UI.ProjectPrices.ExportToPyramid.Succeed'
          };
          return [LoadingActions.loading_end(), SnackbarActions.snackbar_open({ snackbarData: data })];
        })
      ),
    { dispatch: true }
  );

  stopLoadingAndThrowError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          ProjectDataActions.update_project_fail,
          ProjectDataActions.create_entity_fail,
          ProjectDataActions.get_report_fail,
          ProjectDataActions.export_xml_fail,
          ProjectDataActions.delete_entity_fail,
          ProjectDataActions.copy_entity_fail,
          ProjectDataActions.export_magicad_fail,
          ProjectDataActions.export_alldocuments_fail,
          ProjectDataActions.export_project_to_pyramid_fail
        ),
        switchMap(data => {
          return [LoadingActions.loading_end(), ProjectDataActions.throw_error({ error: data.error })];
        })
      ),
    { dispatch: true }
  );

  throwError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          ProjectDataActions.create_project_fail,
          ProjectDataActions.get_project_fail,
          ProjectDataActions.create_entity_fail,
          ProjectDataActions.copy_entity_fail,
          ProjectDataActions.delete_entity_fail,
          ProjectDataActions.throw_error
        ),
        tap(data => {
          throw data.error;
        })
      ),
    { dispatch: false }
  );
}
